// Claude Vision API service for ingredient detection
import { fetchWithRetry, getAuthHeaders } from './apiUtils'

interface ClaudeVisionResponse {
  content: Array<{
    type: string
    text: string
  }>
}

interface IngredientWithConfidence {
  ingredient: string
  confidence: number
}

interface UserPreferences {
  dietary_preferences?: string | null
  food_genres?: string[] | null
}

interface ConfidenceItem {
  name: string
  evidence?: string
}

interface ParsedJsonResponse {
  high_confidence?: ConfidenceItem[]
  medium_confidence?: ConfidenceItem[]
  items?: Array<{
    name?: string
    variant?: string
    confidence?: number
  }>
}

export class ClaudeVisionService {
  // API calls are now handled by backend only - never expose API keys in frontend
  private static readonly BACKEND_URL = process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001')

  static async analyzeIngredients(imageUrls: string[], confidenceThreshold: number = 0.8, userPreferences?: UserPreferences | null, userId?: string): Promise<string[]> {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Starting ingredient analysis via backend with image URLs:', imageUrls)
    }

    try {
      // Use backend API instead of direct Claude API calls (with retry)
      const authHeaders = await getAuthHeaders()
      const response = await fetchWithRetry(
        `${this.BACKEND_URL}/api/analyze-ingredients`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          body: JSON.stringify({
            imageUrls: imageUrls.slice(0, 5), // Limit to 5 images
            dietaryRestrictions: userPreferences?.dietary_preferences,
            cuisinePreferences: userPreferences?.food_genres,
          })
        },
        { maxRetries: 2 } // Fewer retries for user-facing requests
      )

      if (!response.ok) {
        const errorText = await response.text()
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ Backend API error:', response.status, errorText)
        }
        throw new Error(`Backend API error: ${response.status} - ${errorText}`)
      }

      const data = await response.json()
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Backend response:', data)
      }

      if (data.error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Backend returned error:', data.error)
        }
        return this.fallbackAnalysis()
      }

      const ingredients = data.ingredients || []

      if (process.env.NODE_ENV === 'development') {
        console.log('Claude Vision detected ingredients from backend:', {
          threshold: confidenceThreshold,
          totalDetected: ingredients.length,
          result: ingredients
        })
      }

      return ingredients

    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Backend API failed:', error)
        console.log('🔄 Falling back to local analysis...')
      }
      return this.fallbackAnalysis()
    }
  }

  // Legacy methods removed - all Claude API calls now go through backend
  // This prevents API key exposure in frontend code

  private static parseClaudeResponse(data: ClaudeVisionResponse): IngredientWithConfidence[] {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Raw Claude Response:', JSON.stringify(data, null, 2))
      }

      const textContent = data.content
        .filter(item => item.type === 'text')
        .map(item => item.text)
        .join(' ')

      if (process.env.NODE_ENV === 'development') {
        console.log('📝 Extracted text content:', textContent)
      }

      if (!textContent) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('No text content in Claude response')
        }
        return []
      }

      // Try to parse as JSON first
      try {
        const jsonMatch = textContent.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          const jsonData: ParsedJsonResponse = JSON.parse(jsonMatch[0])
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ Parsed JSON data:', jsonData)
          }

          // Handle new structured confidence format
          if (jsonData.high_confidence || jsonData.medium_confidence) {
            const allItems: IngredientWithConfidence[] = []

            // Process high confidence items (0.95 confidence)
            if (jsonData.high_confidence && Array.isArray(jsonData.high_confidence)) {
              const highConfidenceItems = jsonData.high_confidence
                .filter((item: ConfidenceItem) => {
                  return item.name && this.isValidIngredient(item.name.toLowerCase().trim())
                })
                .map((item: ConfidenceItem) => ({
                  ingredient: this.normalizeIngredient(item.name.toLowerCase().trim()),
                  confidence: 0.95
                }))
              allItems.push(...highConfidenceItems)
            }

            // Process medium confidence items (0.8 confidence)
            if (jsonData.medium_confidence && Array.isArray(jsonData.medium_confidence)) {
              const mediumConfidenceItems = jsonData.medium_confidence
                .filter((item: ConfidenceItem) => {
                  return item.name && this.isValidIngredient(item.name.toLowerCase().trim())
                })
                .map((item: ConfidenceItem) => ({
                  ingredient: this.normalizeIngredient(item.name.toLowerCase().trim()),
                  confidence: 0.8
                }))
              allItems.push(...mediumConfidenceItems)
            }

            return allItems
          }

          // Fallback to old format if present
          if (jsonData.items && Array.isArray(jsonData.items)) {
            return jsonData.items
              .filter((item) => {
                // Pre-filter before processing
                if (!item.name || typeof item.confidence !== 'number') return false

                const itemName = item.name.toLowerCase().trim()
                return this.isValidIngredient(itemName)
              })
              .map((item) => {
                // Combine name and variant if present
                let fullName = item.name!.toLowerCase().trim()
                if (item.variant) {
                  fullName = `${item.variant.toLowerCase().trim()} ${fullName}`
                }

                return {
                  ingredient: this.normalizeIngredient(fullName),
                  confidence: Math.max(0, Math.min(1, item.confidence!)) // Clamp between 0-1
                }
              })
          }
        }
      } catch (jsonError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to parse JSON response, falling back to text parsing')
        }
      }

      // Fallback to old comma-separated parsing
      const ingredients = textContent
        .toLowerCase()
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0)
        .filter(item => this.isValidIngredient(item))
        .map(item => this.normalizeIngredient(item))
        .filter((item, index, arr) => arr.indexOf(item) === index) // Remove duplicates
        .map(ingredient => ({ ingredient, confidence: 0.7 })) // Default confidence

      return ingredients

    } catch (error) {
      console.error('Error parsing Claude response:', error)
      return []
    }
  }

  private static isValidIngredient(item: string): boolean {
    const itemLower = item.toLowerCase().trim()

    // Block items that are too short or non-alphabetic
    if (item.length < 2 || !/[a-zA-Z]/.test(item)) {
      return false
    }

    // Targeted blocklist based on user feedback
    const blockedGenericTerms = [
      // Pure categories (too generic)
      'fruit', 'fruits', 'fresh produce', 'vegetables', 'veggies', 'leafy greens',
      'condiments', 'sauces', 'dressings', 'grains', 'nuts', 'herbs', 'spices',
      'oils', 'cereals', 'legumes', 'dairy', 'produce', 'meat', 'seafood',
      'beverages', 'drinks', 'juice', 'citrus fruits', 'root vegetables',

      // Over-generalized terms that should be specific
      'oil', 'seasonings', 'cereal', 'oatmeal', 'granola',

      // Container/non-food items
      'bowls', 'baskets', 'containers', 'bottle', 'jar', 'package', 'can', 'box', 'bag',
      'plastic', 'glass', 'metal', 'wood', 'kitchen', 'fridge', 'refrigerator', 'shelf',

      // Vague descriptors
      'various', 'some', 'many', 'several', 'different', 'other', 'items', 'food',
      'ingredients', 'products', 'goods', 'brand', 'label', 'see', 'visible', 'appears',

      // Known hallucinations that should still be blocked
      'chocolate', 'jam', 'butter'
    ]

    // Block exact matches to generic terms only
    if (blockedGenericTerms.includes(itemLower)) {
      return false
    }

    // Allow everything else (specific items will pass through)
    return true
  }

  private static normalizeIngredient(item: string): string {
    // Clean up and normalize ingredient names
    let normalized = item
      .toLowerCase()
      .trim()
      .replace(/[^\w\s]/g, '') // Remove punctuation
      .replace(/\s+/g, ' ') // Normalize whitespace

    // Enhanced normalizations based on user feedback
    const normalizations: Record<string, string> = {
      // Unify singular/plural
      'tomato': 'tomatoes',
      'onion': 'onions',
      'carrot': 'carrots',
      'apple': 'apples',
      'orange': 'oranges',
      'lemon': 'lemons',
      'lime': 'limes',
      'potato': 'potatoes',
      'yams': 'yam',

      // Collapse oil variants
      'vegetable oil': 'olive oil',
      'cooking oil': 'olive oil',
      'cooking oils': 'olive oil',

      // Consolidate leafy greens
      'leafy greens': 'lettuce',
      'salad greens': 'lettuce',

      // Specific condiments (keep these specific)
      'a1': 'a1 sauce',
      'hellmanns': 'hellmanns mayo',

      // Pepper variants
      'bell pepper': 'peppers',
      'green pepper': 'peppers',
      'red pepper': 'peppers',

      // Cheese variants (keep general for cooking flexibility)
      'cheddar cheese': 'cheese',
      'mozzarella cheese': 'cheese'
    }

    // Apply normalizations
    for (const [key, value] of Object.entries(normalizations)) {
      if (normalized === key || normalized.includes(key)) {
        normalized = value
        break
      }
    }

    return normalized
  }

  // Fallback when API is unavailable
  private static async fallbackAnalysis(): Promise<string[]> {
    if (process.env.NODE_ENV === 'development') {
      console.log('Using fallback ingredient analysis')
    }

    // Simulate analysis time
    await new Promise(resolve => setTimeout(resolve, 1000))

    // Return common ingredients found in most kitchens
    const commonIngredients = [
      'eggs', 'milk', 'bread', 'butter', 'cheese',
      'tomatoes', 'onions', 'garlic', 'carrots',
      'chicken', 'rice', 'pasta', 'olive oil', 'salt'
    ]

    // Return a subset to simulate real detection
    const numIngredients = Math.floor(Math.random() * 6) + 4 // 4-9 ingredients
    const shuffled = [...commonIngredients].sort(() => Math.random() - 0.5)

    return shuffled.slice(0, numIngredients)
  }

  // Utility method to check if backend is available
  static isConfigured(): boolean {
    // API key is now on backend only - always return true since frontend uses backend
    return true
  }

  // Get cost estimate for users (helpful for rate limiting later)
  static estimateCost(numImages: number): { tokens: number; estimatedCost: number } {
    // Rough estimates based on Claude pricing
    const tokensPerImage = 1000 // Average tokens for image analysis
    const costPerMToken = 3 // $3 per million tokens (approximate)

    const totalTokens = numImages * tokensPerImage
    const estimatedCost = (totalTokens / 1000000) * costPerMToken

    return {
      tokens: totalTokens,
      estimatedCost: estimatedCost
    }
  }
}